import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { formMappings } from "../../../config/formMappings";

const COLORS = ["#1b3356", "#de80c2"];

export default function GenderDistributionGraph() {
  const entries = useSelector((state: RootState) => state.formEntries.entries);

  const activeFormName = useSelector(
    (state: RootState) => state.formEntries.activeFormName || ""
  );

  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const genderFieldKey = formMapping?.fieldMappings?.gender || "85tfd3";

  const genderDistribution = entries.reduce((acc, entry) => {
    // First, try to find registrants in the mapped "Registrants" key
    if (entry.meta.Registrants) {
      Object.values(entry.meta.Registrants)
        .filter(
          (registrant) => registrant && (registrant as any)[genderFieldKey]
        )
        .forEach((registrant) => {
          const gender = (registrant as any)[genderFieldKey];
          if (gender) {
            acc[gender] = (acc[gender] || 0) + 1;
          }
        });
    } else {
      // Fallback: look for registrants in keys starting with "9v3kh" (registrants container)
      // or use the top-level gender array
      const registrantsKey = Object.keys(entry.meta).find(
        (key) => key.startsWith("9v3kh") && typeof entry.meta[key] === "object"
      );

      if (registrantsKey) {
        const registrantsContainer = entry.meta[registrantsKey] as any;
        if (registrantsContainer) {
          Object.values(registrantsContainer)
            .filter(
              (item: any) =>
                item &&
                typeof item === "object" &&
                item[genderFieldKey] &&
                item[genderFieldKey] !== "form"
            )
            .forEach((registrant: any) => {
              const gender = registrant[genderFieldKey];
              if (gender) {
                acc[gender] = (acc[gender] || 0) + 1;
              }
            });
        }
      } else {
        // Final fallback: use top-level gender array
        const genderArray = entry.meta[genderFieldKey];
        if (Array.isArray(genderArray)) {
          genderArray.forEach((gender) => {
            if (gender) {
              acc[gender] = (acc[gender] || 0) + 1;
            }
          });
        }
      }
    }
    return acc;
  }, {} as { [gender: string]: number });

  const data = Object.entries(genderDistribution).map(([gender, count]) => ({
    name: gender,
    value: count,
  }));

  return (
    <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6 col-span-2">
      <h1 className="text-xl text-primary text-center">Gender Distribution</h1>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart width={400} height={400}>
          <Pie
            data={data}
            dataKey="value"
            cx="50%"
            cy="50%"
            outerRadius={100}
            fill="#1b3356"
            label={({ name, value }) => `${name}: ${value}`}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
